import React, { useRef } from 'react';
import {
  Box,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../../../theme';
import { useTranslation } from '../../../hooks/useTranslation';

const InstallmentRow = ({ item, total, onDateChange, onValueChange, onRemove, t }) => {
  const dateRef = useRef(null);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      {/* Campo de data */}
      <TextField
        type="date"
        value={item.date}
        onChange={onDateChange(item.id)}
        inputRef={dateRef}
        inputProps={{ 'aria-label': t('simulation.customDates.dateLabel') }}
        InputProps={{
          startAdornment: (
            <InputAdornment
              position="start"
              sx={{ cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation();
                try { dateRef.current?.showPicker?.(); } catch (_) {}
              }}
            >
              <CalendarMonthIcon sx={{ color: '#6B7280', fontSize: 20 }} />
            </InputAdornment>
          ),
        }}
        sx={{
          flex: 3,
          '& .MuiOutlinedInput-root': {
            bgcolor: '#F3F4F6',
            cursor: 'pointer',
            '& fieldset': { borderColor: '#E5E7EB' },
            '&:hover fieldset': { borderColor: '#9CA3AF' },
            '&.Mui-focused fieldset': { borderColor: '#6B7280' },
          },
          '& .MuiInputBase-input': {
            color: '#2C2C2C',
            cursor: 'pointer',
          },
        }}
        onClick={() => { try { dateRef.current?.showPicker?.(); } catch (_) {} }}
      />

      {/* Campo de valor */}
      <TextField
        value={item.value}
        onChange={onValueChange(item.id)}
        placeholder={t('simulation.customDates.valuePlaceholder')}
        inputProps={{ inputMode: 'numeric', 'aria-label': t('simulation.customDates.valueLabel') }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Typography
                sx={{ color: '#6B7280', fontSize: '0.875rem', fontWeight: 600, lineHeight: 1 }}
              >
                R$
              </Typography>
            </InputAdornment>
          ),
        }}
        sx={{
          flex: 1.2,
          '& .MuiOutlinedInput-root': {
            bgcolor: '#F3F4F6',
            '& fieldset': { borderColor: '#E5E7EB' },
            '&:hover fieldset': { borderColor: '#9CA3AF' },
            '&.Mui-focused fieldset': { borderColor: '#6B7280' },
          },
          '& .MuiInputBase-input': { fontSize: '0.875rem', color: '#2C2C2C' },
          '& input::placeholder': { color: '#9CA3AF', opacity: 1, fontSize: '0.8rem' },
        }}
      />

      {/* Botão remover — só aparece com mais de 1 parcela */}
      {total > 1 ? (
        <Tooltip title={t('simulation.customDates.remove')} placement="top">
          <IconButton
            onClick={() => onRemove(item.id)}
            size="small"
            aria-label={t('simulation.customDates.remove')}
            sx={{
              flexShrink: 0,
              color: '#EF4444',
              '&:hover': { bgcolor: 'rgba(239,68,68,0.08)' },
            }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : (
        <Box sx={{ width: 34, flexShrink: 0 }} />
      )}
    </Box>
  );
};

const CustomDateInstallments = ({
  installments = [],
  onAdd,
  onRemove,
  onDateChange,
  onValueChange,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();

  return (
    <Box>
      {/* Header: título à esquerda + botão "Adicionar parcela" à direita */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1.5,
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#2C2C2C' }}>
          {t('simulation.customDates.title')}
        </Typography>

        <Button
          variant="contained"
          onClick={onAdd}
          startIcon={<AddIcon sx={{ fontSize: 18 }} />}
          sx={{
            bgcolor: colors.greenAccent[500],
            color: '#2C2C2C',
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '0.875rem',
            borderRadius: '8px',
            px: 2,
            py: 0.75,
            boxShadow: 'none',
            '&:hover': { bgcolor: '#73C43D', boxShadow: 'none' },
          }}
        >
          {t('simulation.customDates.addInstallment')}
        </Button>
      </Box>

      {installments.map((item) => (
        <InstallmentRow
          key={item.id}
          item={item}
          total={installments.length}
          onDateChange={onDateChange}
          onValueChange={onValueChange}
          onRemove={onRemove}
          t={t}
        />
      ))}
    </Box>
  );
};

export default CustomDateInstallments;
